import React from 'react';
import { observer } from 'src/utils/mobx-react';
import { Input } from 'src/domains/layouts/shared/formComponents/input/Input';
import { CustomDropdown, FilterOptionType } from 'src/components/RealityCheckForm/CustomDropdown';
import { Switcher } from 'src/components/SwitcherNew/Switcher';
import { Badge } from 'src/domains/common/components/badge/badge';
import { ConfigState } from 'src/AppState/ConfigState/ConfigState';
import {
    CALCULATION_TYPE_ARRAY,
    GROSS_CALCULATION_ONLY_FOR_CALENDAR_MESSAGE,
    LIMIT_TYPE_ARRAY,
    NetDepositLimitConfigurationState,
} from 'src/components/DepositLimitTab/NetDepositLimit/NetDepositLimitConfigurationState';
import {
    NetDepositLimitConfigurationPanelDescription,
    NetDepositLimitConfigurationPanelDropdownField,
    NetDepositLimitConfigurationPanelField,
    NetDepositLimitConfigurationPanelFieldLabel,
    NetDepositLimitConfigurationPanelGrid,
    NetDepositLimitConfigurationPanelHelperText,
    NetDepositLimitConfigurationPanelLifetimeValue,
    NetDepositLimitConfigurationPanelTitle,
    NetDepositLimitConfigurationPanelTitleRow,
    NetDepositLimitConfigurationPanelToggle,
    NetDepositLimitConfigurationPanelToggles,
    NetDepositLimitConfigurationPanelWrapper,
} from 'src/components/DepositLimitTab/NetDepositLimit/NetDepositLimitConfigurationPanel.style';

interface NetDepositLimitConfigurationPanelPropsType {
    configState: ConfigState;
    state: NetDepositLimitConfigurationState;
    ageGroupLabel?: string;
    dataTestSuffix?: string;
    showDescription?: boolean;
    titleTooltip?: React.ReactNode;
}

export const NetDepositLimitConfigurationPanel = observer(
    'NetDepositLimitConfigurationPanel',
    ({
        configState,
        state,
        ageGroupLabel,
        dataTestSuffix,
        showDescription = true,
        titleTooltip,
    }: NetDepositLimitConfigurationPanelPropsType): JSX.Element => {
        const calculationTypeConfigurable = configState.config.depositLimitCalculationTypeConfigurable;
        const isLifetimePeriod = state.selectedPeriod.value === '0';
        const gridColumns = calculationTypeConfigurable ? 3 : 2;
        const panelDataTest =
            dataTestSuffix === undefined
                ? 'net-deposit-limit-configuration-panel'
                : `net-deposit-limit-configuration-panel-${dataTestSuffix}`;
        const fieldDataTest = (base: string): string =>
            dataTestSuffix === undefined ? base : `${base}-${dataTestSuffix}`;

        return (
            <NetDepositLimitConfigurationPanelWrapper data-test={panelDataTest}>
                <div>
                    {ageGroupLabel === undefined ? (
                        <NetDepositLimitConfigurationPanelTitleRow>
                            <NetDepositLimitConfigurationPanelTitle>
                                Deposit limit configuration
                            </NetDepositLimitConfigurationPanelTitle>
                            {titleTooltip}
                        </NetDepositLimitConfigurationPanelTitleRow>
                    ) : (
                        <NetDepositLimitConfigurationPanelTitleRow>
                            <Badge
                                className='w-fit cursor-default'
                                dataTest={`${panelDataTest}-age-group-label`}
                                variant='secondary'
                            >
                                {ageGroupLabel}
                            </Badge>
                            {titleTooltip}
                        </NetDepositLimitConfigurationPanelTitleRow>
                    )}
                    {showDescription ? (
                        <NetDepositLimitConfigurationPanelDescription>
                            Configure the net deposit limit that will be applied automatically when a user returns as
                            Previously Positive.
                        </NetDepositLimitConfigurationPanelDescription>
                    ) : ageGroupLabel === undefined ? null : (
                        <NetDepositLimitConfigurationPanelDescription>
                            Limits applied to users aged {ageGroupLabel === 'Over 25' ? '25 and over' : 'under 25'}.
                        </NetDepositLimitConfigurationPanelDescription>
                    )}
                </div>

                <NetDepositLimitConfigurationPanelGrid columns={gridColumns}>
                    <NetDepositLimitConfigurationPanelDropdownField>
                        <NetDepositLimitConfigurationPanelFieldLabel>
                            Restriction period
                        </NetDepositLimitConfigurationPanelFieldLabel>
                        <CustomDropdown
                            dataTest={fieldDataTest('dropdown-restriction-period')}
                            formInputState={state.selectedPeriod}
                            hasInternalLabel={false}
                            onChangeCallback={state.onUpdatePeriod}
                            optionsList={(): Array<FilterOptionType<string>> =>
                                state.createWindowLengthDaysOptionsArray
                            }
                            placeholder='Select period'
                            selectedValue={(): string | null => state.selectedPeriod.value}
                        />
                    </NetDepositLimitConfigurationPanelDropdownField>

                    <NetDepositLimitConfigurationPanelDropdownField>
                        <NetDepositLimitConfigurationPanelFieldLabel>
                            Limit type
                        </NetDepositLimitConfigurationPanelFieldLabel>
                        {isLifetimePeriod ? (
                            <NetDepositLimitConfigurationPanelLifetimeValue>
                                Lifetime
                            </NetDepositLimitConfigurationPanelLifetimeValue>
                        ) : (
                            <CustomDropdown
                                dataTest={fieldDataTest('dropdown-limit-type')}
                                formInputState={state.limitType}
                                hasInternalLabel={false}
                                onChangeCallback={state.onUpdateType}
                                optionsList={(): Array<FilterOptionType<string>> => LIMIT_TYPE_ARRAY}
                                placeholder='Select type'
                                selectedValue={(): string | null => state.limitType.value}
                            />
                        )}
                    </NetDepositLimitConfigurationPanelDropdownField>

                    {calculationTypeConfigurable ? (
                        <NetDepositLimitConfigurationPanelDropdownField>
                            <NetDepositLimitConfigurationPanelFieldLabel>
                                Calculation type
                            </NetDepositLimitConfigurationPanelFieldLabel>
                            <CustomDropdown
                                dataTest={fieldDataTest('dropdown-calculation-type-new')}
                                disabled={state.isCalculationTypeLockedToNet}
                                formInputState={state.calculationType}
                                hasInternalLabel={false}
                                onChangeCallback={state.onUpdateCalculationType}
                                optionsList={(): Array<FilterOptionType<string>> => CALCULATION_TYPE_ARRAY}
                                placeholder='Select calculation'
                                selectedValue={(): string | null => state.calculationType.value}
                            />
                            {state.isCalculationTypeLockedToNet ? (
                                <NetDepositLimitConfigurationPanelHelperText
                                    data-test={fieldDataTest('calculation-type-locked-message')}
                                >
                                    {GROSS_CALCULATION_ONLY_FOR_CALENDAR_MESSAGE}
                                </NetDepositLimitConfigurationPanelHelperText>
                            ) : null}
                        </NetDepositLimitConfigurationPanelDropdownField>
                    ) : null}
                </NetDepositLimitConfigurationPanelGrid>

                <NetDepositLimitConfigurationPanelGrid columns={2}>
                    <NetDepositLimitConfigurationPanelField>
                        <NetDepositLimitConfigurationPanelFieldLabel>
                            Limit value
                        </NetDepositLimitConfigurationPanelFieldLabel>
                        <Input
                            dataTest={fieldDataTest('net-deposit-net-limit-input')}
                            formInputState={state.limitValue}
                            placeholder='Enter limit value'
                        />
                    </NetDepositLimitConfigurationPanelField>

                    <NetDepositLimitConfigurationPanelField>
                        <NetDepositLimitConfigurationPanelFieldLabel>
                            Max limit value
                        </NetDepositLimitConfigurationPanelFieldLabel>
                        <Input
                            dataTest={fieldDataTest('net-deposit-net-limit-max-input')}
                            formInputState={state.limitValueMax}
                            placeholder='Optional max value'
                        />
                    </NetDepositLimitConfigurationPanelField>
                </NetDepositLimitConfigurationPanelGrid>

                <NetDepositLimitConfigurationPanelToggles>
                    {state.isSendEmailWarningVisible ? (
                        <NetDepositLimitConfigurationPanelToggle>
                            <Switcher
                                dataTest={fieldDataTest('net-deposit-net-limit-send-email')}
                                label='Send email warning'
                                state={state.triggersWarningEmail}
                            />
                        </NetDepositLimitConfigurationPanelToggle>
                    ) : null}
                    <NetDepositLimitConfigurationPanelToggle>
                        <Switcher
                            dataTest={fieldDataTest('net-deposit-net-limit-hard-limit')}
                            label='Hard stop'
                            state={state.isHardLimit}
                        />
                    </NetDepositLimitConfigurationPanelToggle>
                </NetDepositLimitConfigurationPanelToggles>
            </NetDepositLimitConfigurationPanelWrapper>
        );
    }
);
